import React, { useState, useEffect } from "react";
import { Card, Modal, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FiInfo, FiX } from "react-icons/fi";
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid,
  Tooltip as ChartTooltip, ResponsiveContainer,
} from "recharts";
import axios from "axios";

const NOMI_MESI = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];

// Restituisce la data locale in formato YYYY-MM-DD senza rischi di timezone
const toLocalDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const SalesChart = () => {
  const [dailyData, setDailyData]   = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [vista, setVista]           = useState("giorno");
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);

  useEffect(() => {
    axios.get("./ordini_urgenti.json").then((res) => {
      const righeRaw = res.data?.CROSS_DOCKING || [];

      // Escludiamo le righe dove qta_dev == 0 E qta_eva < quantita
      const righe = righeRaw.filter((item) => {
        const qtaDev  = parseFloat(item.qta_dev)  || 0;
        const qtaEva  = parseFloat(item.qta_eva)  || 0;
        const quantita = parseFloat(item.quantita) || 0;
        return !(qtaDev === 0 && qtaEva < quantita);
      });

      const oggi = new Date();

      // Stringhe locali — nessun oggetto Date per il confronto → zero timezone shift
      const oggiStr         = toLocalDateStr(oggi);
      const trentaGiorniFa  = new Date(oggi);
      trentaGiorniFa.setDate(trentaGiorniFa.getDate() - 30);
      const trentaGiorniFaStr = toLocalDateStr(trentaGiorniFa);

      const annoCorrente = oggi.getFullYear();
      const meseCorrente = oggi.getMonth();

      // ── VISTA GIORNO: ultimi 30 giorni (confronto stringhe YYYY-MM-DD) ──
      const mappaGiorni = {};
      righe.forEach((item) => {
        const dataStr = item.data_doc ? String(item.data_doc).split(" ")[0] : null;
        if (!dataStr) return;
        // confronto lessicografico YYYY-MM-DD — funziona correttamente senza Date()
        if (dataStr < trentaGiorniFaStr || dataStr > oggiStr) return;

        const importo = parseFloat(item["Importo for"]) || 0;
        mappaGiorni[dataStr] = (mappaGiorni[dataStr] || 0) + importo;
      });

      const arrayGiorni = Object.keys(mappaGiorni).sort().map((key) => {
        const [, mese, giorno] = key.split("-");
        return {
          data:     key,
          etichetta: `${giorno}/${mese}`,
          valore:   Math.round(mappaGiorni[key] * 100) / 100,
        };
      });
      setDailyData(arrayGiorni);

      // ── VISTA ANNO: da gennaio al mese corrente ──
      const mappaMesi = {};
      righe.forEach((item) => {
        const dataStr = item.data_doc ? String(item.data_doc).split(" ")[0] : null;
        if (!dataStr) return;
        const annoItem = parseInt(dataStr.split("-")[0]);
        const meseItem = parseInt(dataStr.split("-")[1]) - 1; // 0-based
        if (annoItem !== annoCorrente) return;
        if (meseItem > meseCorrente) return;

        const importo = parseFloat(item["Importo for"]) || 0;
        mappaMesi[meseItem] = (mappaMesi[meseItem] || 0) + importo;
      });

      const arrayMesi = [];
      for (let m = 0; m <= meseCorrente; m++) {
        arrayMesi.push({
          mese:   NOMI_MESI[m],
          valore: Math.round((mappaMesi[m] || 0) * 100) / 100,
        });
      }
      setMonthlyData(arrayMesi);

    }).catch((err) => console.error(err));
  }, []);

  const formatEuro = (val) =>
    new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(val);

  const xAxisInterval = Math.max(0, Math.ceil(dailyData.length / 12) - 1);

  const renderTooltip = (props) => (
    <Tooltip id="sales-chart-info-tooltip" {...props}>
      <div style={{ textAlign: "left", fontSize: "0.7rem", padding: "5px" }}>
        <strong>MONITORAGGIO IMPORTI:</strong><br />
        • <strong>Vista Mese:</strong> Ultimi 30 giorni (data locale, senza shift UTC).<br />
        • <strong>Vista Anno:</strong> Accumulo mensile progressivo anno corrente.<br />
        • Dati da Cross Docking — importo fornitore.
      </div>
    </Tooltip>
  );

  const renderChartContent = (height = 250) => (
    <div style={{ width: "100%", height }}>
      <ResponsiveContainer width="100%" height="100%">
        {vista === "giorno" ? (
          <AreaChart data={dailyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#1f1f1f" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#000000" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
            <XAxis dataKey="etichetta" axisLine={false} tickLine={false} tick={{ fill: "#999", fontSize: 11 }} dy={10} interval={xAxisInterval} />
            <YAxis tickFormatter={formatEuro} width={80} axisLine={false} tickLine={false} tick={{ fill: "#999", fontSize: 11 }} />
            <ChartTooltip
              contentStyle={{ borderRadius: "10px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", fontSize: "13px" }}
              labelFormatter={(_, payload) => payload?.[0]?.payload?.data || ""}
              formatter={(value) => [formatEuro(value), "Importo Fornitore"]}
              cursor={{ stroke: "#b81f25", strokeWidth: 1, strokeDasharray: "5 5" }}
            />
            <Area type="monotone" dataKey="valore" stroke="#b81f25" strokeWidth={3} fillOpacity={1} fill="url(#colorValue)" animationBegin={0} animationDuration={1200} />
          </AreaChart>
        ) : (
          <BarChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
            <XAxis dataKey="mese" axisLine={false} tickLine={false} tick={{ fill: "#999", fontSize: 11 }} dy={10} />
            <YAxis tickFormatter={formatEuro} width={80} axisLine={false} tickLine={false} tick={{ fill: "#999", fontSize: 11 }} />
            <ChartTooltip
              contentStyle={{ borderRadius: "10px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", fontSize: "13px" }}
              formatter={(value) => [formatEuro(value), "Importo Fornitore"]}
              cursor={{ fill: "rgba(184,31,37,0.08)" }}
            />
            <Bar dataKey="valore" fill="#b81f25" radius={[6, 6, 0, 0]} animationDuration={1000} />
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );

  return (
    <>
      <Card className="card ombra card-home graphic-day hover-effect border-0" style={{ borderRadius: "15px", padding: "1rem" }}>
        <div className="d-flex justify-content-between align-items-center mb-4 px-3">
          <div className="d-flex align-items-center" onClick={() => setShowFullscreenModal(true)} style={{ cursor: "pointer", userSelect: "none" }}>
            <span style={{ fontWeight: "800", fontSize: "0.75rem", color: "#1a1a1a", letterSpacing: "1px", marginRight: "8px" }}>
              CROSS DOCKING MESE/ANNO
            </span>
            <OverlayTrigger placement="bottom" overlay={renderTooltip}>
              <span className="ms-1" onClick={(e) => e.stopPropagation()} style={{ cursor: "pointer" }}>
                <FiInfo size={14} color="#adb5bd" />
              </span>
            </OverlayTrigger>
          </div>
          <button
            className="badge rounded-pill bg-light text-dark border py-2 px-3"
            onClick={() => setVista(vista === "giorno" ? "anno" : "giorno")}
            style={{ cursor: "pointer", fontSize: "0.65rem", fontWeight: "600" }}
          >
            {vista === "giorno" ? "Visualizza anno" : "Visualizza mese"}
          </button>
        </div>
        {renderChartContent(250)}
      </Card>

      <Modal show={showFullscreenModal} onHide={() => setShowFullscreenModal(false)} fullscreen animation>
        <Modal.Body className="p-0 d-flex flex-column" style={{ background: "#f8f9fa" }}>
          <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center shadow-sm" style={{ zIndex: 110 }}>
            <div className="d-flex align-items-center">
              <span style={{ fontWeight: "800", fontSize: "0.9rem", color: "#1a1a1a", letterSpacing: "1px", marginRight: "8px" }}>
                CROSS DOCKING MESE/ANNO
              </span>
              <OverlayTrigger placement="bottom" overlay={renderTooltip}>
                <span style={{ cursor: "pointer" }}><FiInfo size={16} color="#adb5bd" /></span>
              </OverlayTrigger>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                className="badge rounded-pill bg-light text-dark border py-2 px-3"
                onClick={() => setVista(vista === "giorno" ? "anno" : "giorno")}
                style={{ cursor: "pointer", fontSize: "0.75rem", fontWeight: "600" }}
              >
                {vista === "giorno" ? "Visualizza anno" : "Visualizza mese"}
              </button>
              <Button variant="outline-dark" className="d-flex align-items-center gap-1 border-0" onClick={() => setShowFullscreenModal(false)} style={{ borderRadius: "10px", height: "38px" }}>
                <FiX size={16} /> Chiudi
              </Button>
            </div>
          </div>
          <div className="flex-grow-1 p-4 d-flex align-items-center justify-content-center">
            <div className="bg-white p-4 shadow-sm rounded-4 border w-100 h-100 d-flex align-items-center">
              {renderChartContent("80%")}
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default SalesChart;